import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { updateMessage } from '../utils'
import { ChangeEvent, useState } from 'react'
import { Box, Button, TextField } from '@mui/material'
import { PatientMappingForm } from './PatientMappingForm'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { selectPatientInfo, setAddDeviceCount } from '../ProrithmSlice'
import { editProrithmMapping } from '../../../services/ProrithmServices'
import { constants, messages, ProrithmSocketMessage, regex } from '../Constants'

interface MapToHomekareValues {
  executiveName: string
  executivePhone: string
}

interface MapToHomekareFormProps {
  mapTo: string
  mappingId: string | null
  categoryType: string | null
  selectedDeviceId: string
  handleClose: () => void
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
}

export const MapToHomekareForm: React.FC<MapToHomekareFormProps> = ({
  mapTo,
  mappingId,
  categoryType,
  selectedDeviceId,
  handleClose,
  sendMessageOnSocket,
}) => {
  const dispatch = useAppDispatch()
  const patientInfo = useAppSelector(selectPatientInfo)
  const [mapToPatient, setMapToPatient] = useState<string>('')

  const handleMapToPatientChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMapToPatient((event.target as HTMLInputElement)?.value)
  }

  const formik = useFormik<MapToHomekareValues>({
    initialValues: {
      executiveName: '',
      executivePhone: '',
    },
    validationSchema: Yup.object({
      executiveName: Yup.string(),
      executivePhone: Yup.string().matches(
        regex?.validPhone,
        messages?.phoneInvalid
      ),
    }),
    onSubmit: () => {
      handleDeviceMapping()
    },
  })

  const handleDeviceMapping = async () => {
    const payload = {
      categoryType: mapTo,
      patientId: patientInfo?.muhId,
      deviceId: selectedDeviceId,
      homekare: {
        executiveName: formik?.values?.executiveName,
        executivePhoneNumber: formik?.values?.executivePhone,
      },
    }
    const response = await editProrithmMapping(mappingId, payload)
    if (response?.success) {
      dispatch(setAddDeviceCount())
      toast(response?.message || messages?.deviceMappedSuccessfully, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })

      const updateMappingMessage = {
        messageType: constants?.update,
        messageContent: {
          message: updateMessage(categoryType, selectedDeviceId),
          macId: selectedDeviceId,
          category: mapTo,
        },
      }
      sendMessageOnSocket(updateMappingMessage)
      handleClose()
    }
  }

  return (
    <form onSubmit={formik?.handleSubmit}>
      <Box display='flex' flexWrap='wrap' flexDirection='row'>
        <Box minWidth={220} margin={1}>
          <TextField
            fullWidth
            variant='outlined'
            name='executiveName'
            label='Executive Name'
            onBlur={formik?.handleBlur}
            placeholder='Executive Name'
            onChange={formik?.handleChange}
            value={formik?.values?.executiveName}
            error={
              formik?.touched?.executiveName &&
              Boolean(formik?.errors?.executiveName)
            }
            helperText={
              formik?.touched?.executiveName && formik?.errors?.executiveName
            }
          />
        </Box>
        <Box minWidth={220} margin={1}>
          <TextField
            fullWidth
            variant='outlined'
            name='executivePhone'
            label='Executive Phone'
            onBlur={formik?.handleBlur}
            placeholder='Executive Phone'
            onChange={formik?.handleChange}
            value={formik?.values?.executivePhone}
            error={
              formik?.touched?.executivePhone &&
              Boolean(formik?.errors?.executivePhone)
            }
            helperText={
              formik?.touched?.executivePhone && formik?.errors?.executivePhone
            }
          />
        </Box>
      </Box>
      <PatientMappingForm
        mapTo={mapTo}
        mapToPatient={mapToPatient}
        handleMapToPatientChange={handleMapToPatientChange}
      />
      <Box marginY={2} display='flex' justifySelf='flex-end' maxWidth={200}>
        <Button onClick={() => handleClose()}>{constants?.close}</Button>
        <Button type='submit' disabled={!formik?.isValid}>
          {constants?.submit}
        </Button>
      </Box>
    </form>
  )
}
